import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import PhotoPickerModal from '../components/PhotoPickerModal';

// Helper to add auth token to image URLs for protected media
const getAuthenticatedImageUrl = (url) => {
  if (!url) return url;
  const token = api.getAccessToken();
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${token}`;
};

function InvoiceBuilder() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Invoice details
  const [paymentTerms, setPaymentTerms] = useState('due_on_receipt');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  // Add Item Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [itemType, setItemType] = useState('labor');
  const [itemDescription, setItemDescription] = useState('');
  const [itemHours, setItemHours] = useState('');
  const [itemRate, setItemRate] = useState('');
  const [itemCost, setItemCost] = useState('');
  const [itemApplyTax, setItemApplyTax] = useState(false);

  // Photo state
  const [photos, setPhotos] = useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [jobId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobData, userData, previewData, photosData] = await Promise.all([
        api.getJob(jobId),
        api.getProfile(),
        api.getInvoicePreviewData(jobId),
        api.getJobPhotos(jobId).catch(() => []),
      ]);
      setJob(jobData);
      setUser(userData);
      setPhotos(photosData || []);
      // By default, select all photos for invoices (user's guidance: "invoice might need all")
      setSelectedPhotoIds((photosData || []).map(p => p.id));

      // Set defaults from user profile
      setItemRate(userData.default_hourly_rate?.toString() || '');
      setItemApplyTax(userData.tax_enabled || false);

      // Set default due date (14 days from now)
      const defaultDue = new Date();
      defaultDue.setDate(defaultDue.getDate() + 14);
      setDueDate(defaultDue.toISOString().split('T')[0]);

      // Load items from preview data (quote + change orders)
      // API returns 'items' not 'line_items'
      if (previewData?.items?.length > 0) {
        setItems(previewData.items.map((item, index) => ({
          ...parseLineItem(item),
          id: item.source_id || `preview-${index}`,
          source: item.source_type || 'quote',
        })));
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const parseLineItem = (item) => ({
    ...item,
    hours: parseFloat(item.hours) || 0,
    rate: parseFloat(item.rate) || 0,
    cost: parseFloat(item.cost) || 0,
  });

  const formatCurrency = (amount, currency = '$') => {
    const symbol = user?.preferred_currency || currency;
    if (!amount) return `${symbol}0.00`;
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const getItemTotal = (item) => {
    if (item.type === 'labor' || item.type === 'daily_labor' || item.item_type === 'labor' || item.item_type === 'hourly') {
      return (parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0);
    }
    return parseFloat(item.cost) || 0;
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + getItemTotal(item), 0);
  };

  const calculateTax = () => {
    if (!user?.tax_enabled || !user?.default_tax_rate) return 0;
    const taxableAmount = items
      .filter(item => item.apply_tax)
      .reduce((sum, item) => sum + getItemTotal(item), 0);
    return taxableAmount * (user.default_tax_rate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const openAddModal = (type) => {
    setItemType(type);
    setItemDescription('');
    setItemHours('');
    setItemRate(user?.default_hourly_rate?.toString() || '');
    setItemCost('');
    setItemApplyTax(user?.tax_enabled || false);
    setShowAddModal(true);
  };

  const addItem = () => {
    if (!itemDescription.trim()) {
      setError('Please enter a description');
      return;
    }

    if ((itemType === 'labor' || itemType === 'daily_labor') && (!itemHours || !itemRate)) {
      setError(`Please enter ${itemType === 'daily_labor' ? 'days' : 'hours'} and rate`);
      return;
    }

    if ((itemType === 'material' || itemType === 'other') && !itemCost) {
      setError('Please enter cost');
      return;
    }

    const newItem = {
      id: `temp-${Date.now()}`,
      type: itemType,
      item_type: itemType,
      description: itemDescription,
      hours: itemType !== 'material' && itemType !== 'other' ? parseFloat(itemHours) : null,
      rate: itemType !== 'material' && itemType !== 'other' ? parseFloat(itemRate) : null,
      cost: itemType === 'material' || itemType === 'other' ? parseFloat(itemCost) : null,
      apply_tax: itemApplyTax,
      source: 'custom',
    };

    setItems([...items, newItem]);
    setShowAddModal(false);
    setError('');
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleSendInvoice = async () => {
    if (items.length === 0) {
      setError('Please add at least one item to the invoice');
      return;
    }

    setSending(true);
    setError('');
    try {
      // Create the invoice
      const invoiceData = {
        job_id: jobId,
        payment_terms: paymentTerms,
        due_date: dueDate,
        notes: notes,
        line_items: items.map(item => ({
          type: item.type || item.item_type,
          description: item.description,
          hours: item.hours,
          rate: item.rate,
          cost: item.cost,
          apply_tax: item.apply_tax,
        })),
      };

      const invoice = await api.createInvoice(invoiceData);

      // Send the invoice email with selected photos
      await api.sendInvoice(invoice.id, selectedPhotoIds);

      setSuccess('Invoice sent to client!');
      setTimeout(() => {
        navigate(`/jobs/${jobId}`);
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      labor: { label: 'Hourly', bg: 'bg-blue-100', text: 'text-blue-700' },
      hourly: { label: 'Hourly', bg: 'bg-blue-100', text: 'text-blue-700' },
      daily_labor: { label: 'Daily', bg: 'bg-indigo-100', text: 'text-indigo-700' },
      material: { label: 'Material', bg: 'bg-amber-100', text: 'text-amber-700' },
      other: { label: 'Other', bg: 'bg-slate-100', text: 'text-slate-700' },
    };
    return types[type] || types.other;
  };

  const getSourceBadge = (source) => {
    const sources = {
      quote: { label: 'From Quote', bg: 'bg-green-100', text: 'text-green-700' },
      change_order: { label: 'Change Order', bg: 'bg-purple-100', text: 'text-purple-700' },
      custom: { label: 'Custom', bg: 'bg-slate-100', text: 'text-slate-600' },
    };
    return sources[source] || sources.custom;
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading invoice builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link
          to={`/jobs/${jobId}`}
          className="inline-flex items-center text-slate-600 hover:text-primary mb-6 transition"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Job
        </Link>

        {/* Header */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
          <h1 className="text-2xl font-black text-primary mb-2">Create Invoice</h1>
          <p className="text-slate-600">
            <i className="fas fa-briefcase mr-2 text-slate-400"></i>
            {job?.title}
          </p>
          {job?.client_name && (
            <p className="text-slate-500 text-sm mt-1">
              <i className="fas fa-user mr-2 text-slate-400"></i>
              {job.client_name}
            </p>
          )}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl">
            {success}
          </div>
        )}

        {/* Payment Details */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6 p-4">
          <h2 className="font-bold text-primary mb-4">Payment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
              <select
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition bg-white"
              >
                <option value="due_on_receipt">Due on Receipt</option>
                <option value="net_7">Net 7 Days</option>
                <option value="net_14">Net 14 Days</option>
                <option value="net_30">Net 30 Days</option>
                <option value="net_60">Net 60 Days</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
              />
            </div>
          </div>
        </div>

        {/* Add Item Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { type: 'labor', icon: 'fa-clock', label: 'Hourly' },
            { type: 'daily_labor', icon: 'fa-calendar-day', label: 'Daily' },
            { type: 'material', icon: 'fa-boxes', label: 'Material' },
            { type: 'other', icon: 'fa-file-alt', label: 'Other' },
          ].map(({ type, icon, label }) => (
            <button
              key={type}
              onClick={() => openAddModal(type)}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border-2 border-slate-200 hover:border-secondary hover:shadow-md transition"
            >
              <i className={`fas ${icon} text-2xl text-secondary mb-2`}></i>
              <span className="text-sm font-medium text-slate-700">Add {label}</span>
            </button>
          ))}
        </div>

        {/* Line Items List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-bold text-primary">Line Items ({items.length})</h2>
          </div>

          {items.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <i className="fas fa-list text-4xl text-slate-300 mb-3"></i>
              <p>No items added yet</p>
              <p className="text-sm">Items from your quote will appear here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {items.map((item) => {
                const badge = getTypeBadge(item.type || item.item_type);
                const sourceBadge = getSourceBadge(item.source);
                return (
                  <div key={item.id} className="p-4 flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${sourceBadge.bg} ${sourceBadge.text}`}>
                          {sourceBadge.label}
                        </span>
                        {item.apply_tax && (
                          <span className="text-xs text-slate-500">+ Tax</span>
                        )}
                      </div>
                      <p className="text-slate-800">{item.description}</p>
                      {(item.type === 'labor' || item.type === 'daily_labor' || item.item_type === 'labor' || item.item_type === 'hourly') && (
                        <p className="text-sm text-slate-500">
                          {item.hours} {item.type === 'daily_labor' ? 'days' : 'hrs'} × {formatCurrency(item.rate)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-primary">
                        {formatCurrency(getItemTotal(item))}
                      </span>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-slate-400 hover:text-red-500 transition"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Totals */}
        {items.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6 p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span>{formatCurrency(calculateSubtotal())}</span>
              </div>
              {user?.tax_enabled && calculateTax() > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Tax ({user.default_tax_rate}%)</span>
                  <span>{formatCurrency(calculateTax())}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold text-primary pt-2 border-t border-slate-200">
                <span>Total</span>
                <span>{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6 p-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes or payment instructions..."
            rows={3}
            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition resize-none"
          />
        </div>

        {/* Photo Selection */}
        {photos.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-slate-700">
                  <i className="fas fa-camera text-secondary mr-2"></i>
                  Include Photos in PDF
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedPhotoIds.length > 0
                    ? `${selectedPhotoIds.length} of ${photos.length} photo${photos.length !== 1 ? 's' : ''} selected`
                    : 'No photos selected'}
                </p>
              </div>
              <button
                onClick={() => setShowPhotoModal(true)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition font-medium text-sm"
              >
                <i className="fas fa-images mr-2"></i>
                {selectedPhotoIds.length > 0 ? 'Change Selection' : 'Select Photos'}
              </button>
            </div>
            {selectedPhotoIds.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedPhotoIds.slice(0, 5).map((id) => {
                  const photo = photos.find(p => p.id === id);
                  return photo ? (
                    <div key={id} className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                      <img
                        src={getAuthenticatedImageUrl(photo.image_url || photo.image)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null;
                })}
                {selectedPhotoIds.length > 5 && (
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-medium">
                    +{selectedPhotoIds.length - 5}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={handleSendInvoice}
          disabled={sending || items.length === 0}
          className={`w-full py-4 rounded-xl font-bold text-lg transition flex items-center justify-center gap-2 ${
            sending || items.length === 0
              ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
              : 'bg-secondary text-white hover:bg-orange-600 shadow-lg'
          }`}
        >
          {sending ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Sending...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane"></i>
              Send Invoice to Client
            </>
          )}
        </button>
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-primary">
                Add {itemType === 'labor' ? 'Hourly' : itemType === 'daily_labor' ? 'Daily' : itemType === 'material' ? 'Material' : 'Other'} Item
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  placeholder="Enter description..."
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                />
              </div>

              {(itemType === 'labor' || itemType === 'daily_labor') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {itemType === 'daily_labor' ? 'Days' : 'Hours'}
                    </label>
                    <input
                      type="number"
                      value={itemHours}
                      onChange={(e) => setItemHours(e.target.value)}
                      placeholder="0"
                      step="0.5"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {itemType === 'daily_labor' ? 'Daily Rate' : 'Hourly Rate'}
                    </label>
                    <input
                      type="number"
                      value={itemRate}
                      onChange={(e) => setItemRate(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                    />
                  </div>
                </>
              )}

              {(itemType === 'material' || itemType === 'other') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cost</label>
                  <input
                    type="number"
                    value={itemCost}
                    onChange={(e) => setItemCost(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                  />
                </div>
              )}

              {user?.tax_enabled && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={itemApplyTax}
                    onChange={(e) => setItemApplyTax(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-secondary focus:ring-secondary"
                  />
                  <span className="text-slate-700">Apply tax ({user.default_tax_rate}%)</span>
                </label>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-3 rounded-xl font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={addItem}
                className="flex-1 py-3 rounded-xl font-medium bg-secondary text-white hover:bg-orange-600 transition"
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Picker Modal */}
      {showPhotoModal && (
        <PhotoPickerModal
          photos={photos}
          selectedIds={selectedPhotoIds}
          onConfirm={(ids) => {
            setSelectedPhotoIds(ids);
            setShowPhotoModal(false);
          }}
          onClose={() => setShowPhotoModal(false)}
          title="Select Photos for Invoice"
        />
      )}
    </div>
  );
}

export default InvoiceBuilder;
